// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import loadShippingRates from "@guestsupply/App/Handlers/XNG/LoadShippingRates";
import { createHorizontalSmallHeadingAndTextStyles } from "@guestsupply/App/SharedStyles/smallHeadingAndTextStyles";
import { XNGAGE_CUSTOM_WIDGET } from "@guestsupply/XDL/constants";
import { color, xScreen } from "@guestsupply/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

export interface ShippingMethodStyles {
    container?: InjectableCss;
    title?: TypographyProps;
    smallHeadingAndText?: SmallHeadingAndTextStyles;
}

export const shippingMethodStyles: ShippingMethodStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 16px;
        `,
    },
    title: {
        variant: "h4",
        as: "p",
        css: css`
            margin-bottom: 0;
        `,
    },
    smallHeadingAndText: {
        ...createHorizontalSmallHeadingAndTextStyles({
            wrapper: {
                css: css`
                    align-items: center;
                `,
            },
            heading: {
                size: 14,
                weight: "bold",
                css: css`
                    letter-spacing: 0;
                    line-height: 16px;
                `,
            },
            text: {
                size: 14,
                css: css`
                    letter-spacing: 0;
                    line-height: 21px;
                `,
            },
        }),
    },
};

const styles = shippingMethodStyles;

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const sessionIsPickup = getSession(state).fulfillmentMethod === FulfillmentMethod.PickUp;
    const enableWarehousePickup = getSettingsCollection(state).accountSettings.enableWarehousePickup;
    const isPickup =
        enableWarehousePickup || cartState.value?.fulfillmentMethod === FulfillmentMethod.PickUp || sessionIsPickup;

    return {
        cart: cartState.value,
        isPickup,
    };
};

const mapDispatchToProps = {
    loadShippingRates,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const ShippingMethod: FC<Props> = ({ id, cart, loadShippingRates, isPickup }) => {
    const [shippingRates, setShippingRates] = useState<Dictionary<string>>();
    const hasShippingRate = !!shippingRates || Object.keys(shippingRates || {}).length;

    useEffect(() => {
        if (!cart?.carrier?.shipVias?.length) {
            return;
        }
        if (!hasShippingRate) {
            loadShippingRates({
                shipViaIds: cart.carrier.shipVias.map(x => x.id),
                onSuccess: result => {
                    setShippingRates(result);
                },
            });
        }
    }, [cart]);

    if (!cart?.shipVia || !hasShippingRate || isPickup) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container}>
            <Typography {...styles.title}>{translate("Shipping Method")}</Typography>
            <SmallHeadingAndText
                heading={cart?.shipVia?.description || ""}
                text={shippingRates![cart.shipVia.id]}
                extendedStyles={styles.smallHeadingAndText}
            />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ShippingMethod),
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        allowedContexts: ["CheckoutShippingPage", "CheckoutReviewAndSubmitPage"],
        displayName: "Shipping Method",
        fieldDefinitions: [],
    },
};

export default widgetModule;
